/**
 * Admin Pricing CMS — single page, 3 tabs:
 *   1. Plans · edit every plan's price, seats, accounting caps, feature flags
 *   2. Add-ons · edit catalogue prices, names, plan eligibility, visibility
 *   3. Founder Queue · review/verify/reject pending Founding Member applications
 *
 * All mutations hit endpoints that already audit-log via routes_*.py
 */
import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Plus as PlusIcon, ShieldCheck, Save, Check, X, Trash2, BarChart3, RefreshCw, Play, ShieldAlert, Heart, Wrench } from "lucide-react";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const TABS = [
    { id: "plans",   label: "Plans",          icon: CreditCard },
    { id: "addons",  label: "Add-ons",        icon: PlusIcon },
    { id: "founder", label: "Founder Queue",  icon: ShieldCheck },
    { id: "funnel",  label: "Add-on Funnel",  icon: BarChart3 },
    { id: "health",  label: "Schema Health",  icon: Heart },
];

export default function AdminPricingCMS() {
    const [tab, setTab] = useState("plans");
    return (
        <div className="space-y-6" data-testid="admin-pricing-cms">
            <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Pricing CMS</h1>
                <p className="text-sm text-muted-foreground mt-1">Tune plans, add-on prices, and review Founding Member applications. All changes are audit-logged.</p>
            </div>
            <div className="flex gap-1 border-b border-border overflow-x-auto scrollbar-hide" data-testid="cms-tabs">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        data-testid={`cms-tab-${t.id}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                            tab === t.id
                                ? "border-primary text-primary"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}>
                        <t.icon className="w-4 h-4"/> {t.label}
                    </button>
                ))}
            </div>
            {tab === "plans"   && <PlansEditor/>}
            {tab === "addons"  && <AddonsEditor/>}
            {tab === "founder" && <FounderQueue/>}
            {tab === "funnel"  && <AddonFunnel/>}
            {tab === "health"  && <SchemaHealth/>}
        </div>
    );
}

// ─────────── Plans editor ───────────
function PlansEditor() {
    const [plans, setPlans] = useState([]);
    const [busy, setBusy] = useState({});
    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/plans-v2");
            // Only show plans that follow the v2 schema (have `kind`). Legacy plans are archived and not editable here.
            setPlans((data || []).filter(p => p.kind).sort((a, b) => (a.display_order ?? 99) - (b.display_order ?? 99)));
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

    const update = (id, field, value) => {
        setPlans(p => p.map(x => x.id === id ? { ...x, [field]: value } : x));
    };
    const updateFounder = (id, field, value) => {
        setPlans(p => p.map(x => x.id === id
            ? { ...x, founder_config: { ...(x.founder_config || {}), [field]: value } }
            : x));
    };
    // Quotas — null = unlimited, 0 = blocked, n>0 = hard cap. UI exposes a
    // dedicated checkbox for the "Unlimited" state so admins never have to type
    // the word "null".
    const updateQuota = (id, field, value) => {
        setPlans(p => p.map(x => x.id === id
            ? { ...x, quotas: { ...(x.quotas || {}), [field]: value } }
            : x));
    };
    const save = async (plan) => {
        setBusy(p => ({ ...p, [plan.id]: true }));
        try {
            const payload = {
                name: plan.name, description: plan.description,
                price_monthly_inr: Number(plan.price_monthly_inr),
                price_annual_inr: Number(plan.price_annual_inr),
                one_time_price_inr: Number(plan.one_time_price_inr),
                seats_default: Number(plan.seats_default),
                seats_max_per_tenant: Number(plan.seats_max_per_tenant),
                extra_seat_inr_per_month: Number(plan.extra_seat_inr_per_month),
                storage_gb_included: Number(plan.storage_gb_included),
                is_active: !!plan.is_active,
                is_visible_public: !!plan.is_visible_public,
                is_marketing_featured: !!plan.is_marketing_featured,
                display_order: Number(plan.display_order),
            };
            // Quotas — always include so the admin can flip caps and unlimited toggles.
            // null is the sentinel for "unlimited"; numeric fields coerced via Number().
            if (plan.quotas) {
                const sanitized = {};
                for (const [k, v] of Object.entries(plan.quotas)) {
                    sanitized[k] = (v === null || v === "" || v === undefined) ? null : Number(v);
                }
                payload.quotas = sanitized;
            }
            // Include full founder_config so backend doesn't reset other fields to defaults
            if (plan.founder_config) {
                payload.founder_config = {
                    ...plan.founder_config,
                    seats_total: Number(plan.founder_config.seats_total),
                    seats_sold: Number(plan.founder_config.seats_sold),
                    access_duration_years: Number(plan.founder_config.access_duration_years),
                    wallet_credit_inr: Number(plan.founder_config.wallet_credit_inr),
                    wallet_bonus_pct: Number(plan.founder_config.wallet_bonus_pct ?? 0),
                    wallet_bonus_years: Number(plan.founder_config.wallet_bonus_years ?? 0),
                    future_addon_discount_pct: Number(plan.founder_config.future_addon_discount_pct),
                };
            }
            await api.patch(`/admin/plans-v2/${plan.id}`, payload);
            toast.success(`${plan.name} saved`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(p => ({ ...p, [plan.id]: false })); }
    };

    return (
        <div className="space-y-3" data-testid="plans-editor">
            {plans.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Loading plans…</p>}
            {plans.map(p => (
                <div key={p.id} className="bg-card border border-border rounded-xl p-5 shadow-lawos-sm" data-testid={`plan-row-${p.code}`}>
                    <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <input
                                    type="text"
                                    value={p.name || ""}
                                    onChange={e => update(p.id, "name", e.target.value)}
                                    data-testid={`plan-name-${p.code}`}
                                    className="font-heading text-lg font-semibold text-foreground bg-transparent border-b border-transparent focus:border-primary focus:outline-none min-w-[180px] flex-1"
                                />
                                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{p.kind}</span>
                                <code className="text-xs text-muted-foreground">{p.code}</code>
                            </div>
                            <textarea
                                rows={2}
                                value={p.description || ""}
                                onChange={e => update(p.id, "description", e.target.value)}
                                data-testid={`plan-description-${p.code}`}
                                placeholder="Plan description shown to tenants on the upgrade screen…"
                                className="mt-2 w-full text-xs text-muted-foreground bg-transparent border border-border/50 focus:border-primary focus:outline-none rounded px-2 py-1.5 resize-none leading-relaxed"
                            />
                        </div>
                        <button onClick={() => save(p)} disabled={busy[p.id]}
                            data-testid={`save-plan-${p.code}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 disabled:opacity-50">
                            <Save className="w-3 h-3"/> {busy[p.id] ? "Saving…" : "Save"}
                        </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <NumField label="Monthly ₹" v={p.price_monthly_inr} on={v => update(p.id, "price_monthly_inr", v)}/>
                        <NumField label="Annual ₹"  v={p.price_annual_inr}  on={v => update(p.id, "price_annual_inr", v)}/>
                        <NumField label="One-time ₹" v={p.one_time_price_inr} on={v => update(p.id, "one_time_price_inr", v)}/>
                        <NumField label="Seats incl." v={p.seats_default} on={v => update(p.id, "seats_default", v)}/>
                        <NumField label="Max seats"   v={p.seats_max_per_tenant} on={v => update(p.id, "seats_max_per_tenant", v)}/>
                        <NumField label="Extra seat ₹/mo" v={p.extra_seat_inr_per_month} on={v => update(p.id, "extra_seat_inr_per_month", v)}/>
                        <NumField label="Storage GB"  v={p.storage_gb_included} on={v => update(p.id, "storage_gb_included", v)}/>
                        <NumField label="Display order" v={p.display_order} on={v => update(p.id, "display_order", v)}/>
                    </div>
                    <div className="flex gap-4 mt-4 flex-wrap">
                        <Toggle label="Active"   v={p.is_active}   on={v => update(p.id, "is_active", v)}/>
                        <Toggle label="Public"   v={p.is_visible_public} on={v => update(p.id, "is_visible_public", v)}/>
                        <Toggle label="Featured" v={p.is_marketing_featured} on={v => update(p.id, "is_marketing_featured", v)}/>
                    </div>

                    {/* ─── Quotas — enforced server-side by plan_quota.assert_quota ─── */}
                    <div className="mt-4 pt-4 border-t border-border">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold mb-1">Resource Quotas</div>
                        <div className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
                            Hard caps enforced on every API call. <b>Empty / Unlimited</b> = no cap. <b>0</b> = feature blocked.
                            Monthly counters reset on the 1st of every month.
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <QuotaField label="eCourts CNR / month" planId={p.id} field="cnr_sync_per_month"
                                value={p.quotas?.cnr_sync_per_month} on={updateQuota} testidPlan={p.code}/>
                            <QuotaField label="AI credits / month" planId={p.id} field="ai_credits_per_month"
                                value={p.quotas?.ai_credits_per_month} on={updateQuota} testidPlan={p.code}/>
                            <QuotaField label="WhatsApp msgs / month" planId={p.id} field="whatsapp_msgs_per_month"
                                value={p.quotas?.whatsapp_msgs_per_month} on={updateQuota} testidPlan={p.code}/>
                            <QuotaField label="Cases (lifetime)" planId={p.id} field="cases_total"
                                value={p.quotas?.cases_total} on={updateQuota} testidPlan={p.code}/>
                            <QuotaField label="Clients (lifetime)" planId={p.id} field="clients_total"
                                value={p.quotas?.clients_total} on={updateQuota} testidPlan={p.code}/>
                            <QuotaField label="Documents (lifetime)" planId={p.id} field="documents_total"
                                value={p.quotas?.documents_total} on={updateQuota} testidPlan={p.code}/>
                        </div>
                    </div>

                    {p.founder_config && (
                        <div className="mt-4 pt-4 border-t border-border">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold mb-3">Founder Config</div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <NumField
                                    label="Seats total"
                                    v={p.founder_config.seats_total}
                                    on={v => updateFounder(p.id, "seats_total", v)}
                                    testid={`founder-seats-total-${p.code}`}
                                />
                                <NumField
                                    label="Access (years)"
                                    v={p.founder_config.access_duration_years}
                                    on={v => updateFounder(p.id, "access_duration_years", v)}
                                    testid={`founder-access-years-${p.code}`}
                                />
                                <NumField
                                    label="Future addon disc (%)"
                                    v={p.founder_config.future_addon_discount_pct}
                                    on={v => updateFounder(p.id, "future_addon_discount_pct", v)}
                                    testid={`founder-addon-disc-${p.code}`}
                                />
                                <NumField
                                    label="Wallet credit ₹"
                                    v={p.founder_config.wallet_credit_inr}
                                    on={v => updateFounder(p.id, "wallet_credit_inr", v)}
                                    testid={`founder-wallet-credit-${p.code}`}
                                />
                                <NumField
                                    label="Top-up bonus (%)"
                                    v={p.founder_config.wallet_bonus_pct ?? 0}
                                    on={v => updateFounder(p.id, "wallet_bonus_pct", v)}
                                    testid={`founder-bonus-pct-${p.code}`}
                                />
                                <NumField
                                    label="Bonus duration (years)"
                                    v={p.founder_config.wallet_bonus_years ?? 0}
                                    on={v => updateFounder(p.id, "wallet_bonus_years", v)}
                                    testid={`founder-bonus-years-${p.code}`}
                                />
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-2">
                                Seats sold · <b className="text-foreground">{p.founder_config.seats_sold}/{p.founder_config.seats_total}</b>
                                <span className="ml-3 opacity-70">Changes propagate live — landing page, recharge screen and bonus calculation all read these values.</span>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// ─────────── Add-ons editor ───────────
function AddonsEditor() {
    const [addons, setAddons] = useState([]);
    const [busy, setBusy] = useState({});
    const load = useCallback(async () => {
        try { setAddons((await api.get("/admin/addons")).data); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

    const update = (id, field, value) => setAddons(p => p.map(x => x.id === id ? { ...x, [field]: value } : x));
    const save = async (a) => {
        setBusy(p => ({ ...p, [a.id]: true }));
        try {
            await api.patch(`/admin/addons/${a.id}`, {
                code: a.code, name: a.name, description: a.description, category: a.category,
                price_inr_per_month: Number(a.price_inr_per_month),
                quantity_label: a.quantity_label, increment: Number(a.increment) || 1,
                plans_allowed: Array.isArray(a.plans_allowed) ? a.plans_allowed : [],
                display_order: Number(a.display_order),
                is_active: !!a.is_active, is_recommended: !!a.is_recommended, icon: a.icon || "Plus",
            });
            toast.success(`${a.name} saved`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(p => ({ ...p, [a.id]: false })); }
    };
    const archive = async (a) => {
        if (!window.confirm(`Archive "${a.name}"? It will no longer be shown in pricing.`)) return;
        try {
            await api.delete(`/admin/addons/${a.id}`);
            toast.success("Archived");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-3" data-testid="addons-editor">
            {addons.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Loading add-ons…</p>}
            {addons.map(a => (
                <div key={a.id} className="bg-card border border-border rounded-xl p-5 shadow-lawos-sm" data-testid={`addon-row-${a.code}`}>
                    <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="font-heading text-sm font-semibold text-foreground">{a.name}</h3>
                                <code className="text-xs text-muted-foreground">{a.code}</code>
                                <span className="text-[10px] uppercase tracking-[0.18em] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{a.category}</span>
                                {a.is_recommended && <span className="text-[10px] uppercase tracking-[0.18em] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Popular</span>}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{a.description}</p>
                        </div>
                        <div className="flex gap-1.5">
                            <button onClick={() => save(a)} disabled={busy[a.id]} data-testid={`save-addon-${a.code}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 disabled:opacity-50">
                                <Save className="w-3 h-3"/> Save
                            </button>
                            <button onClick={() => archive(a)} data-testid={`archive-addon-${a.code}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 border border-border hover:border-rose-300 hover:text-rose-600 rounded-lg text-xs font-medium">
                                <Trash2 className="w-3 h-3"/>
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <TextField label="Name" v={a.name} on={v => update(a.id, "name", v)}/>
                        <NumField label="₹/month" v={a.price_inr_per_month} on={v => update(a.id, "price_inr_per_month", v)}/>
                        <NumField label="Increment qty" v={a.increment} on={v => update(a.id, "increment", v)}/>
                        <NumField label="Display order" v={a.display_order} on={v => update(a.id, "display_order", v)}/>
                        <TextField label="Quantity label" v={a.quantity_label} on={v => update(a.id, "quantity_label", v)}/>
                        <TextField label="Icon (lucide)" v={a.icon || "Plus"} on={v => update(a.id, "icon", v)}/>
                        <TextField label="Plans allowed (CSV)" v={(a.plans_allowed || []).join(", ")}
                            on={v => update(a.id, "plans_allowed", v.split(",").map(s => s.trim()).filter(Boolean))}/>
                        <TextField label="Description" v={a.description} on={v => update(a.id, "description", v)}/>
                    </div>
                    <div className="flex gap-4 mt-3">
                        <Toggle label="Active"      v={a.is_active}      on={v => update(a.id, "is_active", v)}/>
                        <Toggle label="Recommended" v={a.is_recommended} on={v => update(a.id, "is_recommended", v)}/>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─────────── Founder Queue ───────────
// ─────────── Founder signup wallet-bonus backfill ───────────
// Idempotent one-click sweeper — retroactively grants
// `founder_config.wallet_credit_inr` to founder-plan tenants who activated
// before v2.17.4 (when auto-credit was wired). Same idempotency key as the
// webhook path (`founder-signup:{tenant_id}:{plan_id}`) so clicking twice is
// completely safe: already-credited tenants show `already_credited: true` and
// their wallets remain untouched.
function BackfillFounderBonusButton() {
    const [preview, setPreview] = useState(null);   // dry-run result
    const [result, setResult] = useState(null);     // real-run result
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    const doPreview = async () => {
        setBusy(true); setResult(null);
        try {
            const r = await api.post("/admin/founder-signup-bonus/backfill?dry_run=true");
            setPreview(r.data);
            setOpen(true);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const doApply = async () => {
        setBusy(true);
        try {
            const r = await api.post("/admin/founder-signup-bonus/backfill?dry_run=false");
            setResult(r.data);
            toast.success(`Credited ${r.data.credited_this_run} tenant(s)`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const missing = preview?.missing_credit_count ?? 0;

    return (
        <>
            <div className="flex items-center justify-between gap-3 border border-border rounded-xl p-4 bg-card"
                data-testid="founder-backfill-card">
                <div className="text-xs text-muted-foreground">
                    <div className="text-sm font-medium text-foreground">Founder signup wallet bonus · backfill</div>
                    <div className="mt-0.5">
                        Retroactively credits the CMS &quot;wallet_credit_inr&quot; amount to any founder-plan tenant
                        that was activated before auto-credit was wired. <span className="text-primary">Idempotent</span> — already-credited tenants are skipped.
                    </div>
                </div>
                <button onClick={doPreview} disabled={busy}
                    data-testid="founder-backfill-preview-btn"
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 border border-primary text-primary rounded-lg text-xs font-medium uppercase tracking-[0.14em] hover:bg-primary/10 disabled:opacity-50">
                    <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`}/>
                    {busy ? "Checking…" : "Check + backfill"}
                </button>
            </div>

            {open && preview && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => !busy && setOpen(false)}
                    data-testid="founder-backfill-modal">
                    <div className="bg-background border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                        onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-border">
                            <div className="text-lg text-foreground font-heading">Founder signup bonus · backfill preview</div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {preview.matched_subscriptions} active founder subscription(s) reviewed ·
                                <span className="text-amber-500 font-medium ml-1">{missing}</span> missing credit ·
                                <span className="text-emerald-500 font-medium ml-1">{preview.matched_subscriptions - missing}</span> already credited
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5 space-y-1 text-xs">
                            {(preview.tenants || []).length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    No active founder subscriptions found.
                                </div>
                            ) : (
                                <table className="w-full">
                                    <thead className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                        <tr className="border-b border-border">
                                            <th className="text-left p-2">Tenant</th>
                                            <th className="text-left p-2">Plan</th>
                                            <th className="text-right p-2">Expected ₹</th>
                                            <th className="text-center p-2">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(preview.tenants || []).map((row, i) => {
                                            const applied = result?.tenants?.find(x => x.tenant_id === row.tenant_id);
                                            return (
                                                <tr key={row.tenant_id + i} className="border-b border-border/50" data-testid={`backfill-row-${row.tenant_id}`}>
                                                    <td className="p-2 text-foreground">{row.tenant_name || row.tenant_id}</td>
                                                    <td className="p-2 text-muted-foreground">{row.plan_name}</td>
                                                    <td className="p-2 text-right font-mono">₹{row.expected_amount_inr.toLocaleString("en-IN")}</td>
                                                    <td className="p-2 text-center">
                                                        {row.already_credited ? (
                                                            <span className="inline-flex items-center gap-1 text-emerald-500 text-[11px]" data-testid={`row-status-already-${row.tenant_id}`}>
                                                                <Check className="w-3 h-3"/> Already credited
                                                            </span>
                                                        ) : applied?.credited ? (
                                                            <span className="inline-flex items-center gap-1 text-emerald-500 text-[11px]" data-testid={`row-status-credited-${row.tenant_id}`}>
                                                                <Check className="w-3 h-3"/> Credited now
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 text-amber-500 text-[11px]" data-testid={`row-status-pending-${row.tenant_id}`}>
                                                                <ShieldAlert className="w-3 h-3"/> Will credit
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="p-4 border-t border-border flex items-center justify-between gap-2">
                            <div className="text-[11px] text-muted-foreground">
                                {result ? "Backfill complete. Safe to close." : missing > 0
                                    ? `About to credit ${missing} tenant(s). Already-credited tenants are automatically skipped.`
                                    : "Nothing to do — all founder tenants already have their bonus."}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setOpen(false)} disabled={busy}
                                    data-testid="founder-backfill-cancel"
                                    className="px-3 py-2 border border-border text-muted-foreground hover:text-foreground rounded-lg text-xs disabled:opacity-50">
                                    Close
                                </button>
                                {!result && missing > 0 && (
                                    <button onClick={doApply} disabled={busy}
                                        data-testid="founder-backfill-apply"
                                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-xs uppercase tracking-[0.14em] hover:opacity-90 disabled:opacity-50">
                                        {busy ? "Applying…" : `Credit ${missing} tenant(s)`}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function FounderQueue() {
    const [list, setList] = useState([]);
    const [status, setStatus] = useState("pending");
    const [reason, setReason] = useState({});
    const load = useCallback(async () => {
        try { setList((await api.get(`/admin/founder-verifications?status=${status}`)).data); }
        catch (e) { toast.error(apiError(e)); }
    }, [status]);
    useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

    const act = async (vid, action, name) => {
        const r = reason[vid] || "";
        if (action === "reject" && r.trim().length < 3) {
            toast.error("Reason required (min 3 chars) to reject");
            return;
        }
        if (!window.confirm(`${action === "approve" ? "Verify" : "Reject"} ${name}?`)) return;
        try {
            await api.post(`/admin/founder-verifications/${vid}`, { action, reason: r });
            toast.success(`${action === "approve" ? "Verified" : "Rejected"}`);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-4" data-testid="founder-queue">
            <BackfillFounderBonusButton/>
            <div className="flex items-center gap-2">
                {["pending", "verified", "rejected"].map(s => (
                    <button key={s} onClick={() => setStatus(s)} data-testid={`queue-filter-${s}`}
                        className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.16em] font-medium ${
                            status === s ? "bg-primary text-primary-foreground" : "border border-border text-foreground/70 hover:border-primary"
                        }`}>
                        {s}
                    </button>
                ))}
            </div>
            {list.length === 0 ? (
                <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center text-sm text-muted-foreground" data-testid="queue-empty">
                    No {status} applications.
                </div>
            ) : (
                <div className="space-y-3">
                    {list.map(v => (
                        <div key={v.id} className="bg-card border border-border rounded-xl p-5 shadow-lawos-sm" data-testid={`queue-row-${v.id}`}>
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div>
                                    <div className="font-heading text-base font-semibold text-foreground">{v.name || "—"}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        {v.email} · {v.mobile} · BCI: <span className="font-mono text-primary">{v.bar_council_id}</span> · {v.state}
                                    </div>
                                </div>
                                {status === "pending" && (
                                    <div className="flex gap-2">
                                        <button onClick={() => act(v.id, "approve", v.name)} data-testid={`approve-${v.id}`}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:opacity-90">
                                            <Check className="w-3 h-3"/> Verify
                                        </button>
                                        <button onClick={() => act(v.id, "reject", v.name)} data-testid={`reject-${v.id}`}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-medium hover:opacity-90">
                                            <X className="w-3 h-3"/> Reject
                                        </button>
                                    </div>
                                )}
                            </div>
                            {v.enrollment_cert_url && (
                                <a href={v.enrollment_cert_url} target="_blank" rel="noopener noreferrer"
                                    className="inline-block mt-3 text-xs text-primary hover:underline">View enrollment certificate ↗</a>
                            )}
                            {status === "pending" && (
                                <input value={reason[v.id] || ""} onChange={e => setReason(r => ({ ...r, [v.id]: e.target.value }))}
                                    data-testid={`reason-${v.id}`}
                                    placeholder="Reason (required to reject)…"
                                    className="mt-3 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-xs rounded-lg text-foreground"/>
                            )}
                            {v.review_reason && (
                                <div className="mt-3 text-xs text-muted-foreground"><b>Reviewer note:</b> {v.review_reason}</div>
                            )}
                            <div className="mt-2 text-[10px] text-muted-foreground">Submitted {v.created_at}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─────────── Field primitives ───────────
function NumField({ label, v, on, testid }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
            <input type="number" value={v ?? 0} onChange={e => on(Number(e.target.value))}
                data-testid={testid}
                className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-sm rounded-lg text-foreground"/>
        </label>
    );
}
// Quota input — supports a 3rd state, "Unlimited" (null), beside numeric values.
function QuotaField({ label, planId, field, value, on, testidPlan }) {
    const isUnlimited = value === null || value === undefined;
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center justify-between">
                <span>{label}</span>
                <label className="inline-flex items-center gap-1 cursor-pointer normal-case tracking-normal text-[10px] text-foreground/70 hover:text-foreground">
                    <input type="checkbox" checked={isUnlimited}
                        data-testid={`quota-unlimited-${testidPlan}-${field}`}
                        onChange={e => on(planId, field, e.target.checked ? null : 0)}
                        className="w-3 h-3 accent-primary"/>
                    Unlimited
                </label>
            </span>
            <input type="number" min="0"
                value={isUnlimited ? "" : value}
                placeholder={isUnlimited ? "∞ Unlimited" : "0"}
                disabled={isUnlimited}
                onChange={e => on(planId, field, e.target.value === "" ? null : Number(e.target.value))}
                data-testid={`quota-${testidPlan}-${field}`}
                className={`mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-sm rounded-lg text-foreground ${isUnlimited ? "opacity-50 cursor-not-allowed" : ""}`}/>
        </label>
    );
}
function TextField({ label, v, on }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
            <input value={v ?? ""} onChange={e => on(e.target.value)}
                className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-sm rounded-lg text-foreground"/>
        </label>
    );
}
function Toggle({ label, v, on }) {
    return (
        <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!v} onChange={e => on(e.target.checked)}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30"/>
            <span className="text-xs text-foreground">{label}</span>
        </label>
    );
}


// ─────────── Add-on Funnel widget ───────────
function AddonFunnel() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [config, setConfig] = useState(null);          // {grace_days, default_grace_days, min, max}
    const [graceEdit, setGraceEdit] = useState(null);    // local edit buffer
    const [savingGrace, setSavingGrace] = useState(false);

    const load = useCallback(async () => {
        try {
            const [funnel, cfg] = await Promise.all([
                api.get(`/admin/addons/funnel?days=${days}`).then(r => r.data),
                api.get(`/admin/addons/renewal/config`).then(r => r.data),
            ]);
            setData(funnel);
            setConfig(cfg);
            setGraceEdit(cfg.grace_days);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [days]);

    useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

    async function saveGrace() {
        if (graceEdit === null || graceEdit === config?.grace_days) return;
        setSavingGrace(true);
        try {
            const { data: r } = await api.patch("/admin/addons/renewal/config", { grace_days: graceEdit });
            toast.success(`Grace period updated · ${r.previous}d → ${r.grace_days}d`);
            setConfig(c => ({ ...c, grace_days: r.grace_days }));
        } catch (e) { toast.error(apiError(e)); }
        finally { setSavingGrace(false); }
    }

    async function runRenewalNow() {
        if (!window.confirm("Force a renewal pass right now? This creates Razorpay payment links + emails firm owners for any add-ons whose next_charge_at is due.")) return;
        setRunning(true);
        try {
            const { data: r } = await api.post("/admin/addons/renewal/run-now");
            toast.success(`Renewal pass complete · created ${r.created} · emailed ${r.emailed} · suspended ${r.suspended}`);
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunning(false); }
    }

    if (loading && !data) return <div className="text-sm text-muted-foreground" data-testid="funnel-loading">Loading funnel…</div>;
    if (!data) return null;

    const t = data.totals || {};
    const peak = Math.max(1, ...((data.daily_series || []).map(d => d.starts)));

    return (
        <div className="space-y-5" data-testid="addon-funnel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="inline-flex items-center gap-1 bg-muted/30 rounded-lg p-1" data-testid="funnel-window">
                    {[7, 30, 90].map(d => (
                        <button key={d} onClick={() => setDays(d)}
                            data-testid={`funnel-window-${d}`}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                                days === d ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"
                            }`}>
                            Last {d}d
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} data-testid="funnel-refresh"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border hover:border-primary rounded-lg text-xs font-medium text-foreground">
                        <RefreshCw className="w-3.5 h-3.5"/> Refresh
                    </button>
                    <button onClick={runRenewalNow} disabled={running} data-testid="funnel-run-renewal"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 disabled:opacity-50">
                        <Play className="w-3.5 h-3.5"/> {running ? "Running…" : "Run renewal pass now"}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="funnel-kpis">
                <KpiCard label="Checkout starts" value={t.starts ?? 0} testId="kpi-starts"/>
                <KpiCard label="Paid" value={t.paid ?? 0} tone="success" testId="kpi-paid"/>
                <KpiCard label="Pending" value={t.pending ?? 0} tone="warn" testId="kpi-pending"/>
                <KpiCard label={`Abandoned (>${config?.grace_days ?? 2}d)`} value={t.abandoned ?? 0} tone="muted" testId="kpi-abandoned"/>
                <KpiCard label="Conversion" value={`${data.conversion_rate_pct ?? 0}%`} tone="success" testId="kpi-conversion"/>
            </div>

            {/* Grace-period editor */}
            <div className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="grace-period-editor">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Renewal grace period</div>
                    <div className="text-sm text-foreground mt-1">
                        Addons unpaid for <span className="font-mono font-semibold text-foreground">{config?.grace_days ?? "—"} day(s)</span> past due are auto-suspended.
                        <span className="text-xs text-muted-foreground ml-2">(default {config?.default_grace_days ?? 2}d, range {config?.min_grace_days ?? 0}–{config?.max_grace_days ?? 30}d)</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <input type="number"
                        min={config?.min_grace_days ?? 0}
                        max={config?.max_grace_days ?? 30}
                        value={graceEdit ?? 0}
                        onChange={e => setGraceEdit(Math.max(config?.min_grace_days ?? 0, Math.min(config?.max_grace_days ?? 30, parseInt(e.target.value || "0", 10))))}
                        data-testid="grace-days-input"
                        className="w-20 px-3 py-2 bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none rounded-lg text-sm font-mono text-foreground text-right"/>
                    <span className="text-xs text-muted-foreground">days</span>
                    <button onClick={saveGrace} disabled={savingGrace || graceEdit === config?.grace_days}
                        data-testid="grace-days-save"
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 disabled:opacity-40">
                        <Save className="w-3.5 h-3.5"/> {savingGrace ? "Saving…" : "Save grace"}
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <KpiCard label="Revenue (pro-rata, paid)" value={`₹${(t.revenue_inr ?? 0).toLocaleString("en-IN")}`} tone="success" big testId="kpi-revenue"/>
                <KpiCard label="Failed checkout orders" value={t.failed ?? 0} tone={(t.failed ?? 0) > 0 ? "danger" : "muted"} big testId="kpi-failed"/>
            </div>

            <div className="rounded-xl border border-border bg-card p-4" data-testid="funnel-daily">
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">Daily activity · starts vs paid</div>
                {data.daily_series.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6 text-center">No activity in the selected window.</div>
                ) : (
                    <div className="flex items-end gap-1 h-28">
                        {data.daily_series.map(d => (
                            <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date} · ${d.starts} starts · ${d.paid} paid · ₹${d.revenue_inr.toLocaleString("en-IN")}`}>
                                <div className="w-full flex flex-col-reverse items-center gap-px" style={{height: "100%"}}>
                                    <div className="w-full bg-emerald-500/70 rounded-sm" style={{height: `${(d.paid / peak) * 100}%`}}/>
                                    <div className="w-full bg-primary/30 rounded-sm" style={{height: `${((d.starts - d.paid) / peak) * 100}%`}}/>
                                </div>
                                <span className="text-[9px] text-muted-foreground">{d.date.slice(5)}</span>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex items-center gap-4 mt-3 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-emerald-500/70 rounded-sm"/>Paid</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-primary/30 rounded-sm"/>Started but unpaid</span>
                </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-4" data-testid="funnel-top-addons">
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">Top add-ons by checkout starts</div>
                {data.top_addons.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-3">No checkouts yet.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                                <th className="text-left py-2">Add-on</th>
                                <th className="text-right py-2">Starts</th>
                                <th className="text-right py-2">Paid</th>
                                <th className="text-right py-2">Conv.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_addons.map(a => (
                                <tr key={a.code} className="border-t border-border/50" data-testid={`funnel-top-${a.code}`}>
                                    <td className="py-2">
                                        <div className="font-medium text-foreground">{a.name}</div>
                                        <div className="text-[10px] text-muted-foreground font-mono">{a.code}</div>
                                    </td>
                                    <td className="text-right text-foreground">{a.starts}</td>
                                    <td className="text-right text-emerald-600">{a.paid}</td>
                                    <td className="text-right font-mono">{a.conversion_rate}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {data.failure_reasons.length > 0 && (
                <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4" data-testid="funnel-failures">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-rose-600 mb-3">Failure reasons</div>
                    <ul className="space-y-1.5">
                        {data.failure_reasons.map((f, i) => (
                            <li key={i} className="flex justify-between text-sm">
                                <span className="text-foreground font-mono text-xs truncate">{f.reason_excerpt}</span>
                                <span className="text-rose-600 font-semibold">{f.count}×</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function KpiCard({ label, value, tone = "default", big = false, testId }) {
    const toneCls = {
        success: "text-emerald-600",
        warn: "text-amber-600",
        danger: "text-rose-600",
        muted: "text-muted-foreground",
        default: "text-foreground",
    }[tone] || "text-foreground";
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
            <div className={`mt-1.5 font-mono font-semibold ${toneCls} ${big ? "text-2xl" : "text-xl"}`}>{value}</div>
        </div>
    );
}

/* ─────────── Schema Health sentinel ─────────── */
function SchemaHealth() {
    const [data, setData] = useState(null);
    const [healing, setHealing] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/schema-health");
            setData(data);
        } catch (e) { toast.error(apiError(e)); }
    }, []);

    useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

    async function healOne(code) {
        if (!window.confirm(`Auto-heal "${code}"? This will mutate database rows. Continue?`)) return;
        setHealing(code);
        try {
            const { data: r } = await api.post("/admin/schema-health/heal", { code });
            if ((r.rows_healed || 0) > 0) {
                toast.success(`Healed ${r.rows_healed} row(s) for ${code}`);
            } else {
                toast.success(`Already clean for ${code}`);
            }
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setHealing(null); }
    }

    async function healAll() {
        if (!window.confirm("Auto-heal ALL fixable schema issues? This will mutate database rows across all collections.")) return;
        setHealing("__all__");
        try {
            const { data: r } = await api.post("/admin/schema-health/heal", {});
            const total = r.total_healed || 0;
            if (total > 0) {
                toast.success(`Healed ${total} row(s) across ${Object.keys(r.healed).length} checks`);
            } else {
                toast.success("Already clean · nothing to heal");
            }
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setHealing(null); }
    }

    if (!data) return <div className="text-sm text-muted-foreground" data-testid="schema-health-loading">Scanning…</div>;
    const allClear = data.all_clear;
    return <SchemaHealthBody data={data} allClear={allClear} healing={healing} healOne={healOne} healAll={healAll} load={load}/>;
}

function SchemaHealthBody({ data, allClear, healing, healOne, healAll, load }) {
    const ctrl = useTableControls(data.checks, {
        accessors: { check: (c) => `${c.label} ${c.code}` },
    });
    return (
        <div className="space-y-5" data-testid="schema-health">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <p className="text-sm text-muted-foreground">
                        Daily smoke-detector for silent-corruption bugs. Each check counts rows missing critical fields
                        that would cause downstream 500s, silent skips, or compliance leaks.
                    </p>
                    <div className="text-[10px] text-muted-foreground mt-1">Last scan: {new Date(data.scanned_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} data-testid="schema-health-refresh"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border hover:border-primary rounded-lg text-xs font-medium text-foreground">
                        <RefreshCw className="w-3.5 h-3.5"/> Re-scan
                    </button>
                    {!allClear && (
                        <button onClick={healAll} disabled={healing !== null} data-testid="schema-health-heal-all"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 disabled:opacity-40">
                            <Wrench className="w-3.5 h-3.5"/>{healing === "__all__" ? "Healing…" : "Heal all"}
                        </button>
                    )}
                </div>
            </div>

            {allClear ? (
                <div className="rounded-xl border-2 border-emerald-500/30 bg-emerald-500/5 p-8 text-center" data-testid="schema-health-clear">
                    <Check className="w-10 h-10 text-emerald-600 mx-auto mb-2"/>
                    <div className="text-xl font-semibold text-emerald-700">All clear · 0 broken rows</div>
                    <div className="text-xs text-emerald-700/70 mt-2">All 8 schema invariants holding across every tenant.</div>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="schema-health-summary">
                    <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-rose-600">Total broken</div>
                        <div className="mt-1.5 text-2xl font-mono font-semibold text-rose-700">{data.total_broken_rows}</div>
                    </div>
                    <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-rose-600">Critical checks</div>
                        <div className="mt-1.5 text-2xl font-mono font-semibold text-rose-700">{data.critical_checks_failing}</div>
                    </div>
                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600">High checks</div>
                        <div className="mt-1.5 text-2xl font-mono font-semibold text-amber-700">{data.high_checks_failing}</div>
                    </div>
                    <div className="rounded-xl border border-border bg-card p-4">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Checks run</div>
                        <div className="mt-1.5 text-2xl font-mono font-semibold text-foreground">{data.checks.length}</div>
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-border bg-card overflow-x-auto">
                <FilterChip ctrl={ctrl} className="m-3"/>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                            <SortableTh field="check" ctrl={ctrl} className="text-left px-4 py-2">Check</SortableTh>
                            <SortableTh field="severity" ctrl={ctrl} className="text-center px-2 py-2 w-24" align="center">Severity</SortableTh>
                            <SortableTh field="broken_count" ctrl={ctrl} className="text-right px-2 py-2 w-24" align="right">Broken</SortableTh>
                            <th className="text-right px-4 py-2 w-32 text-xs uppercase tracking-wider font-medium">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ctrl.rows.map(c => (
                            <tr key={c.code} className="border-t border-border/50" data-testid={`health-row-${c.code}`}>
                                <td className="px-4 py-3">
                                    <div className="text-foreground font-medium">{c.label}</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{c.impact}</div>
                                    <div className="text-[10px] text-muted-foreground/70 font-mono mt-0.5">{c.code}</div>
                                </td>
                                <td className="text-center">
                                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${
                                        c.severity === "critical" ? "bg-rose-500/10 text-rose-700"
                                            : c.severity === "high" ? "bg-amber-500/10 text-amber-700"
                                            : "bg-muted text-muted-foreground"
                                    }`}>{c.severity}</span>
                                </td>
                                <td className="text-right font-mono">
                                    {c.broken_count === 0 ? (
                                        <span className="text-emerald-600">0 ✓</span>
                                    ) : c.broken_count < 0 ? (
                                        <span className="text-rose-600">err</span>
                                    ) : (
                                        <span className="text-rose-600 font-semibold">{c.broken_count}</span>
                                    )}
                                </td>
                                <td className="text-right px-4 py-3">
                                    {c.broken_count > 0 && c.auto_heal_available && (
                                        <button onClick={() => healOne(c.code)} disabled={healing !== null}
                                            data-testid={`heal-${c.code}`}
                                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary text-primary-foreground rounded text-[10px] uppercase tracking-wider font-semibold hover:opacity-90 disabled:opacity-40">
                                            <Wrench className="w-3 h-3"/>{healing === c.code ? "Healing…" : "Heal"}
                                        </button>
                                    )}
                                    {c.broken_count > 0 && !c.auto_heal_available && (
                                        <span className="text-[10px] text-muted-foreground italic">Manual triage</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

